import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrencies,
    getCurrentUserIsGuest,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import User from "@insite/mobius/Icons/User";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Drawer, { DrawerPresentationProps } from "@MasterBluePrint/MobiusOverrides/Drawer/Drawer";
import { MappedLink } from "@MasterBluePrint/MobiusOverrides/Menu/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@MasterBluePrint/MobiusOverrides/PanelMenu/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@MasterBluePrint/MobiusOverrides/PanelMenu/PanelRow";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        firstName: state.context.session?.firstName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage"),
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    QuickBuyNavigationRow?: PanelRowPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "rectangle",
        color: "secondary",
        size: 50,
        css: css`
            background: none;
            border: none;
            padding: 0 0 0 .625rem;
            &:focus {
                background: none;
                border: none;
            }
            &:active {
                background: none;
                border: none;
            }
        `,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -.125remrem;
                overflow-x: hidden;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                padding-bottom: 0;
                // background: ${getColor("common.backgroundContrast")};
                > a > span > span, a > span {
                    color: ${themeColors.colorPrimary};
                    font-family: 'Roboto', sans-serif;
                    font-size: ${themeFontSizes.size15};
                    font-weight: 500;
                    line-height: 1.125rem;
                }
            }
            &:last-child {
                margin-bottom: .31250625rem;
            }
            margin-top: 1.25rem;
            padding: 0;

            &.cl-container {
                padding-bottom: 0;
                margin-top: 0;

                > nav {
                    > button {
                        span:first-child {
                            font-size: .9375rem;
                            font-weight: 500;
                            color: #002554;

                            > span {
                                font-size: .875rem;
                                margin-top: 0;
                            }

                            svg {
                                font-size: .875rem;
                                color: #002554;
                                width: 16px;
                                height: 19px;
                            }
                        }
                    }
                    > div {
                        > div:not(:first-child) {
                            button {
                                padding: 17px 20px !important;
                                color: #2E384D;
                                font-family: 'Lato', sans-serif;
                                font-size: 15px;
                                letter-spacing: 0.17px;
                                line-height: 18px;

                                &.current {
                                    color: #2e384d;
                                    background-color: #f3f8fe;
                                    text-decoration: underline;
                                    font-weight: bold;
                                    span {
                                        font-weight: bold;
                                        color: #2e384d;
                                    }
                                }
                                &:focus:not(:focus-visible) {
                                    outline: none;
                                }
                                &:focus-visible {
                                    outline: 2px dashed #2060ad;
                                }
                            }
                        }
                    }
                }

                div[class^="PanelMenuStyle"],
                div[class*=" PanelMenuStyle"] {
                    button {
                        width: 100%;
                        cursor: pointer;
                        padding: 10px 20px;
                        border-top: 1px solid #c2d1d9;
                        color: #2e384d;
                        background: #fff;
                        border-left: none;

                        span {
                            font-size: .9375rem;
                            font-weight: 500;
                            color: #002554;

                            > span {
                                font-size: .9375rem;
                            }
                        }

                        svg {
                            font-size: .9375rem;
                            color: #002554;
                        }
    
                        &:first-child {
                            border-top: none;
                        }
    
                        &:hover {
                            color: #2e384d;
                            background: #f3f8fe;
                            font-weight: 700;
                            text-decoration: underline;
                        }
                    }
                }
            }

            
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                > button {
                    width: calc(100% - 2px);
                    border-bottom: 1px solid ${themeColors.colorBorderNav};
                    color: ${themeColors.colorPrimary};
                    font-size: ${themeFontSizes.size15};
                    font-weight: 500;
                }
                > button > span {
                    font-size: ${themeFontSizes.size15};
                    font-weight: 500;
                    color: ${themeColors.colorPrimary};
                    line-height: 1.125rem;
                    > span {
                        color: ${themeColors.colorPrimary};
                    }
                }
                ~ {
                    div {
                        a {
                            width: calc(100% - 2px);
                            border-bottom: 1px solid ${themeColors.colorBorderNav};
                            color: ${themeColors.colorPrimary};
                            font-size: ${themeFontSizes.size15};
                            font-weight: 500;

                            > span {
                                font-size: ${themeFontSizes.size15};
                                font-weight: 500;
                                color: ${themeColors.colorPrimary};
                            }
                        }
                    }
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 1.375rem;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -.125remrem;
            margin-right: .625rem;
            width: 1.5rem;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "capitalize",
        color: "common.background",
    },
    punchOutUserRowText: {
        transform: "capitalize",
        color: "common.background",
    },
    mainNavigationRow: {
        color: "common.backgroundContrast",
        css: css`
            margin: 0;
            border-bottom: 1px solid ${themeColors.colorBorderNav};
        `,
    },
    QuickBuyNavigationRow: {
        css: css`
            margin: 0;
            text-align: center;
            border-bottom: 1px solid ${themeColors.colorBorderNav};
        `,
    },
    mainNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -.125remrem;
            margin-right: .625rem;
            width: 1.5rem;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    punchOutNavigationRowIcon: {
        src: "User",
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -.125remrem;
            margin-right: .625rem;
            width: 1.5rem;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "capitalize",
        color: "common.background",
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            transform: "uppercase",
        },
        css: css`
            margin: .125rem;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -.250625rem;
            margin-right: .625rem;
            width: 1.5rem;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    signOutRowText: {
        transform: "capitalize",
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer: FC<Props> = props => {
    const [isOpen, setIsOpen] = useState(false);
    const openDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: true });
        props.setInitialValues({});
    };

    const closeDrawer = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        props.isPunchOutSession ? props.cancelPunchOut() : props.signOut();
    };

    const {
        quickOrderLink,
        links,
        showQuickOrder,
        currencies,
        currentCurrencyId,
        currentCurrencySymbol,
        languages,
        currentLanguage,
        setLanguage,
        setCurrency,
        userName,
        firstName,
        isGuest,
        myAccountPageLink,
        signInUrl,
        currentLocation,
        showCustomerMenuItem,
        fulfillmentLabel,
        isPunchOutSession,
        headerLinkListLinkFields,
        history,
        createAccountUrl,
        currentUserIsGuest,
    } = props;

    const currentPageUrl = currentLocation.pathname;
    const returnUrl = currentLocation.pathname + currentLocation.search;
    const signInPageUrl = `${signInUrl?.url}?returnUrl=${encodeURIComponent(returnUrl)}`;
    return (
        <>
            <Button onClick={openDrawer} {...styles.menuTriggerButton} data-test-selector="expandMobileMenu">
                <ButtonIcon src={Menu} />
                <VisuallyHidden id="drawerTitle">{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                draggable
                position="left"
                {...styles.drawer}
                isOpen={props.drawerIsOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
            >
                <StyledSection {...styles.drawerSectionWrapper}>
                    {userName && !isGuest && !isPunchOutSession ? (
                        <PanelMenu
                            currentUrl={currentPageUrl}
                            panelTrigger={
                                <PanelRow hasChildren {...styles.mainNavigationRow}>
                                    <StyledSpan {...styles.panelSectionWrapper}>
                                        <Typography {...styles.userRowTypography}>Welcome {firstName}</Typography>
                                    </StyledSpan>
                                </PanelRow>
                            }
                            menuItems={myAccountPageLink?.children ? myAccountPageLink?.children : []}
                            maxDepth={3}
                            closeOverlay={closeDrawer}
                            layer={0}
                            {...styles.panelMenu}
                        />
                    ) : isPunchOutSession ? (
                        <PanelRow {...styles.mainNavigationRow}>
                            <StyledSpan {...styles.panelSectionWrapper}>
                                <Icon {...styles.punchOutNavigationRowIcon} />
                                <Typography {...styles.punchOutUserRowText}>{userName}</Typography>
                            </StyledSpan>
                        </PanelRow>
                    ) : (
                        <PanelRow
                            isCurrent={currentPageUrl === signInUrl?.url}
                            onClick={closeDrawer}
                            href={signInPageUrl}
                            {...styles.mainNavigationRow}
                        >
                            <StyledSpan {...styles.panelSectionWrapper}>
                                {/* <Icon {...styles.mainNavigationRowIcon} /> */}
                                <Typography {...styles.userRowTypography}>{translate("Sign In")}</Typography>
                            </StyledSpan>
                        </PanelRow>
                    )}
                    {(!userName || currentUserIsGuest) && (
                        <PanelRow
                            isCurrent={currentPageUrl === createAccountUrl?.url}
                            href={createAccountUrl?.url}
                            onClick={closeDrawer}
                            {...styles.mainNavigationRow}
                        >
                            <StyledSpan {...styles.panelSectionWrapper}>
                                <Typography {...styles.userRowTypography}>{translate("Register")}</Typography>
                            </StyledSpan>
                        </PanelRow>
                    )}
                    {(!userName || currentUserIsGuest) && (
                        <PanelRow href="/OrderStatus" {...styles.mainNavigationRow} onClick={closeDrawer}>
                            <StyledSpan {...styles.panelSectionWrapper}>
                                <Typography {...styles.userRowTypography}>Order Status</Typography>
                            </StyledSpan>                            
                        </PanelRow>
                    )}
                    {/* covers MainNavigation functionality */}
                    {links.map((link, index) =>
                        link.children && link.children != null && link?.showDirectLinkInMobile! === false ? (
                            <PanelMenu
                                key={Math.random()}
                                currentUrl={currentPageUrl}
                                closeOverlay={closeDrawer}
                                panelTrigger={
                                    <PanelRow onClick={closeDrawer} hasChildren {...styles.mainNavigationRow}>
                                        <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                                    </PanelRow>
                                }
                                menuItems={link.children}
                                maxDepth={link.maxDepth || 3}
                                layer={0}
                                {...styles.panelMenu}
                            />
                        ) : (
                            <PanelRow onClick={closeDrawer} key={Math.random()} href={link.url} {...styles.mainNavigationRow}>
                                <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                            </PanelRow>
                        ),
                    )}
                    {showQuickOrder && quickOrderLink && (
                        <PanelRow
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            {/* SDEVP-15 : Changed name to Quick Buy */}
                            <Typography {...styles.mainNavigationRowTypography}> {translate("Quick Buy")}</Typography>
                        </PanelRow>
                    )}
                </StyledSection>
                <StyledSection {...styles.drawerSectionWrapper} className="cl-container">
                    <SelectorMenu
                        dataTestSelector="mobileCurrencySelector"
                        options={currencies?.map(c => {
                            return {
                                title: c.currencyCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setCurrency({ currencyId: c.id });
                                        closeDrawer();
                                    },
                                },
                                isCurrentItem: c.id === currentCurrencyId,
                            };
                        })}
                        closeModal={closeDrawer}
                        currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                        currentOptionIcon={((typeof window !== "undefined") && (window as any).showCurrencyIcon) && <Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>}
                    />
                    <SelectorMenu
                        dataTestSelector="mobileLanguageSelector"
                        options={languages?.map(l => {
                            return {
                                title: l.description.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setLanguage({ languageId: l.id });
                                        closeDrawer();
                                    },
                                },
                                isCurrentItem: l.description === currentLanguage?.description,
                            };
                        })}
                        currentOption={currentLanguage?.description}
                        closeModal={closeDrawer}
                        currentOptionIcon={
                            currentLanguage?.imageFilePath ? (
                                <>{((typeof window !== "undefined") && (window as any).showLanguageIcon) && <LogoImage src={currentLanguage.imageFilePath} alt="" />}</>
                            ) : (
                                <>{((typeof window !== "undefined") && (window as any).showLanguageIcon) && <Icon src={Globe} {...styles.menuRowIcon} />}</>
                            )
                        }
                    />
                    {showCustomerMenuItem && (
                        <PanelMenu
                            {...styles.panelMenu}
                            panelTrigger={
                                <PanelRow hasChildren {...styles.logoLinks} color="common.accent">
                                    <Typography {...styles.logoLinks?.typographyProps}>
                                        <Icon src={MapPin} {...styles.menuRowIcon} />
                                        {fulfillmentLabel}
                                    </Typography>
                                </PanelRow>
                            }
                            layer={0}
                            closeOverlay={closeDrawer}
                        >
                            {isPunchOutSession ? (
                                <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                    {createWidgetElement("Header/AddressDrawerPunchOutCustomer", {
                                        fields: {},
                                    })}
                                </PanelRow>
                            ) : (
                                <PanelRow tabIndex={-1} {...styles.changeCustomerRow}>
                                    <GridContainer {...styles.changeCustomerRowContainer}>
                                        <GridItem {...styles.fulfillmentMethodGridItem}>
                                            {createWidgetElement("Header/AddressDrawerFulfillmentMethodSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.addressesGridItem}>
                                            {createWidgetElement("Header/AddressDrawerSelectCustomer", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.pickUpAddressGridItem}>
                                            {createWidgetElement("Header/AddressDrawerPickUpLocationSelector", {
                                                fields: {},
                                            })}
                                        </GridItem>
                                        <GridItem {...styles.applyButtonGridItem}>
                                            {createWidgetElement("Header/AddressDrawerApplyButton", { fields: {} })}
                                        </GridItem>
                                    </GridContainer>
                                </PanelRow>
                            )}
                        </PanelMenu>
                    )}
                    {userName && !isGuest && (
                        <PanelRow onClick={onSignOutHandler} {...styles.signOutRow}>
                            <Typography {...styles.signOutRowText}>
                                {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: .625rem;
    height: 1.375rem;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption: React.ReactNode;
    currentOptionIcon: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren {...styles.logoLinks}>
            <StyledSpan {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationDrawer));